import getHistory from '../history';
import { ErrorPageTypes } from '../types';
import type { SettingsKeys } from '../features/settings/settingsSlice';
import { config } from '../../config';

const history = getHistory();

interface WithBasePathType {
  withBasePath?: boolean;
}

const nullStringToNull = (str: string | null) => {
  if (str === 'null' || !str) {
    return null;
  }
  return str;
};

// We use hash router. By default, we append hash to the url.
const mayBeAppendHash = (url: string, withBasePath = false): string => {
  if (withBasePath) {
    const basePath = window.location.pathname;
    return `${basePath}#${url}`;
  }
  return url;
};

export const getPluginDemoUrl = (token: string) => {
  return `${config.appBaseUrl}/plugin-test.html?token=${token}`;
};

interface GetTeamUrlType extends WithBasePathType {
  teamId: string | string[] | null;
}
export const getTeamUrl = ({ teamId, withBasePath }: GetTeamUrlType): string =>
  mayBeAppendHash(`/library/${teamId}/recents`, withBasePath);

export const goToTeamUrl = (options: GetTeamUrlType) =>
  history.push(getTeamUrl(options));

interface GetFolderUrlType extends WithBasePathType {
  teamId: string;
  folderId: string;
}
export const getFolderUrl = ({
  teamId,
  folderId,
  withBasePath,
}: GetFolderUrlType) =>
  mayBeAppendHash(`/library/${teamId}/${folderId}`, withBasePath);

export const goToFolderUrl = (options: GetFolderUrlType) => {
  history.push(getFolderUrl(options));
};

interface GetFileUrlType {
  teamId: string;
  parentId: string | null;
  fileId: string;
}
export const getFileUrl = ({
  teamId,
  parentId = 'default',
  fileId,
  withBasePath,
}: GetFileUrlType & {
  view?: 'list' | 'grid';
} & WithBasePathType) => {
  return mayBeAppendHash(
    `/library/${teamId}/${nullStringToNull(parentId) || 'default'}/${fileId}`,
    withBasePath,
  );
};

export const goToFileUrl = (options: GetFileUrlType) => {
  history.push(getFileUrl({ ...options }));
};

/**
 * Get the correct file URL based on context and ownership.
 *
 * @param context
 *   - `'library'`: user is browsing a folder they have access to — always use /library/ URLs.
 *   - `'shared'`: file was directly shared without folder access — always use /shared/ URLs.
 *   - `'auto'`: decide based on ownership (fallback for sidebar / ambiguous contexts).
 */
interface GetFileUrlForUserType extends WithBasePathType {
  teamId: string;
  parentId: string | null | undefined;
  fileId: string;
  ownerId: string | undefined;
  currentUserId: string | undefined;
  context?: 'library' | 'shared' | 'auto';
}
export const getFileUrlForUser = ({
  teamId,
  parentId,
  fileId,
  ownerId,
  currentUserId,
  context = 'auto',
  withBasePath,
}: GetFileUrlForUserType) => {
  // In library context the user has folder access — always use /library/ URLs
  if (context === 'library') {
    return getFileUrl({
      teamId,
      parentId: parentId || 'default',
      fileId,
      withBasePath,
    });
  }

  // In shared context, always use /shared/ URLs
  if (context === 'shared') {
    if (parentId) {
      return mayBeAppendHash(
        `/shared/${teamId}/f/${parentId}/${fileId}`,
        withBasePath,
      );
    }
    if (!ownerId) {
      return getFileUrl({
        teamId,
        parentId: parentId || 'default',
        fileId,
        withBasePath,
      });
    }
    return mayBeAppendHash(
      `/shared/${teamId}/u/${ownerId}/${fileId}`,
      withBasePath,
    );
  }

  // Auto: decide based on ownership
  if (!ownerId || (currentUserId && ownerId === currentUserId)) {
    return getFileUrl({
      teamId,
      parentId: parentId || 'default',
      fileId,
      withBasePath,
    });
  }
  if (parentId) {
    return mayBeAppendHash(
      `/shared/${teamId}/f/${parentId}/${fileId}`,
      withBasePath,
    );
  }
  return mayBeAppendHash(
    `/shared/${teamId}/u/${ownerId}/${fileId}`,
    withBasePath,
  );
};

export const goToManageSpaceUrl = () => {
  history.push('/settings/spaces');
};

export const goToSignupUrl = () => {
  history.push('/signup');
};

export const goToSinginUrl = () => {
  history.push('/signin');
};

export const goToDashboardUrl = () => {
  history.push('/dashboard');
};

export const goToRecordingUrl = () => {
  history.push('/recording');
};

export const getSignoutUrl = () => {
  return '/signout';
};

export const goToContactUsUrl = () => {
  goToContactUsUrl();
};

export const getContactSalesUrl = ({ withBasePath }: WithBasePathType = {}) =>
  mayBeAppendHash('/contact-sales', withBasePath);

export const goToContactSales = () => {
  history.push(getContactSalesUrl({}));
};

export const getContactSupportUrl = ({ withBasePath }: WithBasePathType = {}) =>
  mayBeAppendHash('/contact-support', withBasePath);

export const goToContactSupport = () => {
  history.push(getContactSupportUrl({}));
};

export const getFogottenPasswordUrl = ({ withBasePath }: WithBasePathType) => {
  return mayBeAppendHash('/password/forgot', withBasePath);
};
export const goToForgotPassword = (options: WithBasePathType) => {
  history.push(
    getFogottenPasswordUrl({ withBasePath: !!options.withBasePath }),
  );
};

export const getSettingsUrl = ({
  settingType = 'user',
  withBasePath,
}: WithBasePathType & {
  settingType?: SettingsKeys;
}) => {
  return mayBeAppendHash(`/settings/${settingType}`, withBasePath);
};

export const goToResetPasswordUrl = (token: string) => {
  history.push(`/password/reset?token=${token}`);
};

interface GoToErrorPageType {
  errorType: ErrorPageTypes;
}
export const goToErrorPage = ({ errorType }: GoToErrorPageType) => {
  switch (errorType) {
    case 'resetPasswordTokenExpiredError':
      history.push('/resetpasswordtoken_expired');
      break;
    case 'verificationTokenExpiredError':
      history.push('/verificationtoken_expired');
      break;
    case 'requestExpiredError':
      history.push('/expired-request');
      break;
    default:
      history.push('/');
  }
};

export const getReferAndEarnUrl = () => {
  return 'https://screenapp.tolt.io/';
};

export const goToLandingPage = () => {
  history.push('/');
};

export const goToReferralUrl = () => {
  window.open(getReferAndEarnUrl(), '_blank');
};

export const goToNewDashboardUrl = () => {
  history.push('/library');
};

export const getSearchUrl = ({ withBasePath }: WithBasePathType = {}) =>
  mayBeAppendHash('/search', withBasePath);

export const goToSearchUrl = ({
  searchParams,
}: {
  searchParams: URLSearchParams;
}) => {
  history.push({
    pathname: getSearchUrl(),
    search: searchParams.toString(),
  });
};

export const getJoinUrl = (joinCode: string) => {
  return `${config.appBaseUrl}/#/dashboard?joinCode=${joinCode}`;
};

export const getShareUrl = (shareId: string) => {
  // Dynamically build URL based on current pathname
  const { origin, pathname } = window.location;
  const basePath =
    pathname === '/' || pathname === '' ? '' : pathname.replace(/\/$/, '');
  return `${origin}${basePath}/v/${shareId}`;
};

export const getEmbedUrl = (shareId: string) => {
  return `${config.appEmbedBaseUrl}/v/${shareId}`;
};

/**
 * Get the navigation path for a video share URL (for use with window.location.href or history.push)
 * @param shareId - The share ID of the video
 * @param queryParams - Optional query parameters object (e.g., { start: 0, end: 10, t: 5 })
 */
export const getShareNavigationPath = (
  shareId: string,
  queryParams?: Record<string, string | number>,
): string => {
  // Get the base path from current pathname
  const { pathname } = window.location;

  // If pathname is root "/" or empty, use "/v/shareId"
  // Otherwise use the pathname as base: "/app/" -> "/app/v/shareId"
  const basePath =
    pathname === '/' || pathname === '' ? '' : pathname.replace(/\/$/, '');
  let path = `${basePath}/v/${shareId}`;

  if (queryParams && Object.keys(queryParams).length > 0) {
    const params = new URLSearchParams();
    Object.entries(queryParams).forEach(([key, value]) => {
      params.append(key, String(value));
    });
    path += `?${params.toString()}`;
  }

  return path;
};

/**
 * Get the URL for the Multi-Ask AI page
 */
export const getMultiAskAIUrl = ({ withBasePath }: WithBasePathType = {}) => {
  return mayBeAppendHash('/multi-ask-ai', withBasePath);
};

/**
 * Navigate to the Multi-Ask AI page
 */
export const goToMultiAskAIUrl = () => {
  history.push(getMultiAskAIUrl({}));
};

/**
 * URL-based Dialog/Popup Utilities
 *
 * These utilities enable opening dialogs via URL query parameters,
 * allowing for shareable links and proper browser history integration.
 */

/**
 * Open a dialog by adding a query parameter to the current URL
 * @param dialogType - The type of dialog to open (e.g., 'contact-support')
 */
export const openDialogUrl = (dialogType: string) => {
  const currentUrl = new URL(window.location.href);
  const params = new URLSearchParams(currentUrl.search);
  params.set('dialog', dialogType);

  // Update URL without page reload
  history.push({
    pathname: window.location.hash.substring(1).split('?')[0],
    search: params.toString(),
  });
};

/**
 * Close the currently open dialog by removing the dialog query parameter
 */
export const closeDialogUrl = () => {
  const currentUrl = new URL(window.location.href);
  const params = new URLSearchParams(currentUrl.search);
  params.delete('dialog');

  // Update URL without page reload
  history.push({
    pathname: window.location.hash.substring(1).split('?')[0],
    search: params.toString(),
  });
};

/**
 * Get a URL with a dialog parameter
 * @param path - The path to navigate to
 * @param dialogType - The type of dialog to open
 * @param withBasePath - Whether to include the base path
 */
export const getUrlWithDialog = ({
  path,
  dialogType,
  withBasePath = false,
}: {
  path: string;
  dialogType: string;
  withBasePath?: boolean;
}): string => {
  const url = mayBeAppendHash(path, withBasePath);
  return `${url}?dialog=${dialogType}`;
};

/**
 * Navigate to a URL and open a dialog
 * @param path - The path to navigate to
 * @param dialogType - The type of dialog to open
 */
export const goToUrlWithDialog = ({
  path,
  dialogType,
}: {
  path: string;
  dialogType: string;
}) => {
  history.push(`${path}?dialog=${dialogType}`);
};
